import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { addAccount, archiveAccount, updateAccount } from '../firebase/firebaseUtils';
import { Button, Card, CardContent, Table, Thead, Tbody, Tr, Th, Td, TrH, TableContainer, AccountsContainer, StyledList, WidgetListItem, Select, ModalContainer, ModalContent } from './StyledComponents';
import EditAccountModal from './EditAccountModal';
import AddAccountModal from './AddAccountModal';
import AccountTransactionsModal from './AccountTransactionsModal';
import { database } from '../firebase/firebaseConfig';
import { ref, onValue } from 'firebase/database';

const Accounts = ({ userId, accounts, setAccounts, setCustomizeWidgetsHandler }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [transactionsAccount, setTransactionsAccount] = useState(null);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAccountType, setNewAccountType] = useState('');

  const defaultWidgets = [
    { id: 'Bank', enabled: true, width: 1, height: 1 },
    { id: 'Loan', enabled: true, width: 1, height: 1 },
    { id: 'Asset', enabled: true, width: 1, height: 1 },
    { id: 'Expense', enabled: true, width: 1, height: 1 },
    { id: 'Income', enabled: true, width: 1, height: 1 },
  ];

  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('accountsWidgets');
    return saved ? JSON.parse(saved) : defaultWidgets;
  });

  useEffect(() => {
    localStorage.setItem('accountsWidgets', JSON.stringify(widgets));
  }, [widgets]);

  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    if (setCustomizeWidgetsHandler) {
      setCustomizeWidgetsHandler(() => () => setShowConfig(true));
      return () => setCustomizeWidgetsHandler(null);
    }
  }, [setCustomizeWidgetsHandler]);
  const toggleWidget = (id) => {
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, enabled: !w.enabled } : w)));
  };

  const moveWidget = (index, direction) => {
    setWidgets((prev) => {
      const newWidgets = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newWidgets.length) return prev;
      const [moved] = newWidgets.splice(index, 1);
      newWidgets.splice(newIndex, 0, moved);
      return newWidgets;
    });
  };

  const updateWidgetSize = (id, dimension, value) => {
    setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, [dimension]: value } : w)));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const openTransactionsModal = (account) => {
    setTransactionsAccount(account);
    setIsTransactionsModalOpen(true);
  };

  const openEditFromTransactions = () => {
    setEditingAccount(transactionsAccount);
    setIsTransactionsModalOpen(false);
    setIsModalOpen(true);
  };

  const openAddModal = (accountType) => {
    setNewAccountType(accountType);
    setIsAddModalOpen(true);
  };

  const handleAddNewAccount = async (newAccount) => {
    setIsAddModalOpen(false);
    try {
      await addAccount(userId, newAccount);
      const accountsRef = ref(database, `users/${userId}/accounts`);
      onValue(accountsRef, (snapshot) => {
        const data = snapshot.val() || {};
        setAccounts(Object.entries(data).reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {}));
      }, { onlyOnce: true });
    } catch (error) {
      console.error("Error adding account: ", error);
    }
  };

  const handleArchiveAccount = async (accountId) => {
    try {
      await archiveAccount(userId, accountId);
      setAccounts((prev) => {
        if (!prev?.[accountId]) {
          return prev;
        }
        return {
          ...prev,
          [accountId]: {
            ...prev[accountId],
            archived: true,
          },
        };
      });
    } catch (error) {
      console.error("Error archiving account: ", error);
    }
  };

  const handleUpdateAccount = (updatedAccount) => {
    if (!updatedAccount.id) {
      console.error('Error: Account ID is missing');
      return;
    }
    updateAccount(userId, updatedAccount.id, {
      name: updatedAccount.name,
      balance: parseFloat(updatedAccount.balance),
      type: updatedAccount.type,
      color: updatedAccount.color,
      availabilityType: updatedAccount.availabilityType,
      availableFromAge: updatedAccount.availableFromAge,
      availableFromDate: updatedAccount.availableFromDate,
    }).then(() => {
      // Refresh or update the accounts list here
    }).catch((error) => {
      console.error("Error updating account: ", error);
    });
    setIsModalOpen(false);
  };


  const renderWidget = (widget, layoutHeight) => {
    const { id, width = 1, height = 1 } = widget;
    const displayHeight = layoutHeight ?? height;
    return (
      <AccountsCard key={id} $width={width} $height={displayHeight}>
        <h2>
          <span>{id} Accounts</span>
          <Button onClick={() => openAddModal(id)}>+</Button>
        </h2>
        <AccountsTableContainer $height={displayHeight}>
          <Table>
            <Thead>
              <TrH>
                <Th>Name</Th>
                <Th>Balance</Th>
                <Th>Actions</Th>
              </TrH>
            </Thead>
            <Tbody>
              {Object.entries(accounts || {})
                .filter(([accId, account]) => account.type === id && !account.archived)
                .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true }))
                .map(([accId, account]) => (
                  <Tr key={accId} onClick={() => openTransactionsModal({ id: accId, ...account })}>
                    <Td>{account.name}</Td>
                    <Td style={{ color: account.balance < 0 ? '#c26166' : 'inherit' }}>
                      {formatCurrency(account.balance)}
                    </Td>
                    <Td>
                      <Button onClick={(e) => { e.stopPropagation(); openTransactionsModal({ id: accId, ...account }); }}>View</Button>
                      <Button onClick={(e) => { e.stopPropagation(); handleArchiveAccount(accId); }}>Archive</Button>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </AccountsTableContainer>
      </AccountsCard>
    );
  };

  return (
    <>
      <EditAccountModal
        account={editingAccount}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleUpdateAccount}
      />
      <AccountTransactionsModal
        isOpen={isTransactionsModalOpen}
        onClose={() => setIsTransactionsModalOpen(false)}
        account={transactionsAccount}
        accounts={accounts}
        userId={userId}
        onEditAccount={openEditFromTransactions}
      />
      <AddAccountModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddNewAccount}
        accountType={newAccountType}
      />
      <AccountsContainer>
        {showConfig && (
          <ModalContainer onClick={() => setShowConfig(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h2>Widgets</h2>
              <CardContent>
                <StyledList>
                  {widgets.map((w, index) => (
                    <WidgetListItem key={w.id}>
                      <input
                        type="checkbox"
                        checked={w.enabled}
                        onChange={() => toggleWidget(w.id)}
                      />
                      <span>{w.id}</span>
                      <Select
                        aria-label="width"
                        value={w.width}
                        onChange={(e) => updateWidgetSize(w.id, 'width', parseInt(e.target.value))}
                      >
                        <option value={1}>1x width</option>
                        <option value={2}>2x width</option>
                        <option value={3}>3x width</option>
                      </Select>
                      <Select
                        aria-label="height"
                        value={w.height}
                        onChange={(e) => updateWidgetSize(w.id, 'height', parseInt(e.target.value))}
                      >
                        <option value={1}>1x height</option>
                        <option value={2}>2x height</option>
                      </Select>
                      <Button onClick={() => moveWidget(index, -1)}>↑</Button>
                      <Button onClick={() => moveWidget(index, 1)}>↓</Button>
                    </WidgetListItem>
                  ))}
                </StyledList>
              </CardContent>
              <Button onClick={() => setShowConfig(false)}>Close</Button>
            </ModalContent>
          </ModalContainer>
        )}
        <AccountsGrid>
          {getBalancedWidgets(widgets).map(({ widget, layoutHeight }) =>
            renderWidget(widget, layoutHeight)
          )}
        </AccountsGrid>
      </AccountsContainer>
    </>
  );
};

export default Accounts;

const BASE_CARD_WIDTH = 320;
const BASE_TABLE_HEIGHT = 320;
const CARD_CHROME_HEIGHT = 140;

const getWidthBasis = (width) => `${Math.max(1, width) * BASE_CARD_WIDTH}px`;
const getTableHeight = (height) => `${Math.max(1, height) * BASE_TABLE_HEIGHT}px`;
const getCardMinHeight = (height) =>
  `${CARD_CHROME_HEIGHT + Math.max(1, height) * BASE_TABLE_HEIGHT}px`;

const getBalancedWidgets = (widgets) => {
  const enabled = widgets.filter((w) => w.enabled);
  return enabled.map((widget, index) => {
    const prev = enabled[index - 1];
    const next = enabled[index + 1];
    let layoutHeight = widget.height || 1;

    if (widget.width === 1) {
      if (prev && prev.width >= 2 && (prev.height || 1) > layoutHeight) {
        layoutHeight = prev.height || layoutHeight;
      }
      if (next && next.width >= 2 && (next.height || 1) > layoutHeight) {
        layoutHeight = next.height || layoutHeight;
      }
    }

    return { widget, layoutHeight };
  });
};

const AccountsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  align-items: stretch;
`;

const AccountsCard = styled(Card)`
  margin: 0;
  min-width: 260px;
  flex: 1 1 ${({ $width }) => getWidthBasis($width)};
  max-width: 100%;
  min-height: ${({ $height }) => getCardMinHeight($height)};

  h2 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 12px 0;
  }
`;

const AccountsTableContainer = styled(TableContainer)`
  flex: 1 1 auto;
  height: ${({ $height }) => getTableHeight($height)};
  min-height: ${({ $height }) => getTableHeight($height)};
  max-height: ${({ $height }) => getTableHeight($height)};
  padding: 0;
  margin-top: 12px;
  overflow-x: auto;
  overflow-y: auto;

  ${Table} {
    margin: 0;
  }
`;
